import { Close, ImageOutlined, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const formatPrice = (price) => typeof price === 'number'
  ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(price)
  : null;

const ProductDetail = ({ product, open, onClose, onChoose }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  useEffect(() => {
    setSelectedImageIndex(0);
    setImageViewerOpen(false);
  }, [product]);

  if (!product) return null;

  const images = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  const selectedImage = images[selectedImageIndex];
  const showPreviousImage = () => setSelectedImageIndex((current) => (current - 1 + images.length) % images.length);
  const showNextImage = () => setSelectedImageIndex((current) => (current + 1) % images.length);

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>{product.name}</DialogTitle>
      <IconButton aria-label="Cerrar detalle" onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12 }}><Close /></IconButton>
      <DialogContent dividers sx={{ p: 0 }}>
        {selectedImage ? (
          <Box component="img" src={selectedImage} alt={product.name} onClick={() => setImageViewerOpen(true)} sx={{ display: 'block', width: '100%', maxHeight: 360, objectFit: 'cover', bgcolor: '#f5eeea', cursor: 'zoom-in' }} />
        ) : (
          <Box sx={{ height: 260, display: 'grid', placeItems: 'center', bgcolor: '#f5eeea', color: '#9a7d73' }}><ImageOutlined sx={{ fontSize: 52 }} /></Box>
        )}
        {images.length > 1 && (
          <Stack direction="row" spacing={1} sx={{ p: 1.5, overflowX: 'auto' }}>
            {images.map((image, index) => (
              <Box key={`${image}-${index}`} component="button" type="button" aria-label={`Ver imagen ${index + 1}`} aria-pressed={index === selectedImageIndex} onClick={() => setSelectedImageIndex(index)} sx={{ flex: '0 0 64px', width: 64, height: 64, p: 0, border: index === selectedImageIndex ? '2px solid #a52a4b' : '1px solid #eadfdb', borderRadius: 1, overflow: 'hidden', bgcolor: '#f5eeea', cursor: 'pointer' }}>
                <Box component="img" src={image} alt="" sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            ))}
          </Stack>
        )}
        <Stack spacing={2} sx={{ p: 2.5 }}>
          {formatPrice(product.price) && <Typography variant="h6" sx={{ color: '#a52a4b', fontWeight: 800 }}>{formatPrice(product.price)}</Typography>}
          {product.description && <Typography variant="body2" color="text.secondary">{product.description}</Typography>}
          {product.variants.map((variant, index) => (
            <Box key={`${variant.color}-${index}`}>
              <Typography variant="subtitle2">{variant.color || 'Variante'}</Typography>
              {variant.sizes.length > 0 && <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>{variant.sizes.map((size) => <Chip key={size} label={size} size="small" variant="outlined" />)}</Stack>}
            </Box>
          ))}
          <Divider />
          <Button variant="contained" onClick={() => onChoose(product)} sx={{ alignSelf: 'flex-start', bgcolor: '#a52a4b', '&:hover': { bgcolor: '#851f3b' } }}>Elegir</Button>
        </Stack>
      </DialogContent>
    </Dialog>
    <Dialog open={imageViewerOpen} onClose={() => setImageViewerOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2, bgcolor: '#171312' } }}>
      <IconButton aria-label="Cerrar imágenes" onClick={() => setImageViewerOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}><Close /></IconButton>
      <DialogContent sx={{ position: 'relative', display: 'grid', placeItems: 'center', p: { xs: 1, sm: 2 }, minHeight: { xs: 320, sm: 500 } }}>
        {selectedImage && <Box component="img" src={selectedImage} alt={product.name} sx={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} />}
        {images.length > 1 && <>
          <IconButton aria-label="Imagen anterior" onClick={showPreviousImage} sx={{ position: 'absolute', left: 12, top: '50%', color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}><NavigateBefore /></IconButton>
          <IconButton aria-label="Imagen siguiente" onClick={showNextImage} sx={{ position: 'absolute', right: 12, top: '50%', color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}><NavigateNext /></IconButton>
          <Typography sx={{ position: 'absolute', bottom: 12, color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', px: 1, py: 0.5, borderRadius: 1 }} variant="caption">{selectedImageIndex + 1} / {images.length}</Typography>
        </>}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProductDetail;